/**
 * @fileoverview Filtro por Setores (filas) do modal de Filtros de /tickets,
 * com o MESMO layout do filtro por Etiquetas (FilterPopover compartilhado).
 *
 * `selectedQueueIds` é a lista de ids, onde `NO_QUEUE_SENTINEL` (0) representa
 * "Sem fila". Diferente das etiquetas, lista vazia aqui NÃO significa "todos":
 * o Aplicar do modal exige ao menos um setor (ou "Sem fila") — por isso o
 * estado zero mostra um aviso em vez de "Exibindo todos os itens".
 * `showNoQueue` falso tira a opção "Sem fila" (quem não vê as conversas sem fila).
 */

import React, { useMemo } from "react";
import { i18n } from "../../translate/i18n";
import { FilterPopover } from "../FilterPopover";

/**
 * "Sem fila" na lista de ids selecionados. Quem consome (TicketsListCustom)
 * converte pra `null` antes de mandar pro backend — ids reais começam em 1.
 */
export const NO_QUEUE_SENTINEL = 0;

export function QueuesFilter({
  userQueues,
  selectedQueueIds = [],
  onChange,
  placeholder,
  fullWidth = false,
  disabled = false,
  className,
  placement,
  showNoQueue = true,
}) {
  const options = useMemo(
    () => (userQueues || []).map((q) => ({ id: q.id, name: q.name, color: q.color })),
    [userQueues]
  );
  const noQueueSelected = selectedQueueIds.includes(NO_QUEUE_SENTINEL);
  const selectedIds = useMemo(
    () => selectedQueueIds.filter((id) => id !== NO_QUEUE_SENTINEL),
    [selectedQueueIds]
  );

  // Reconstrói a lista completa preservando o sentinela quando marcado.
  const emit = (ids, withNoQueue) => onChange([...(withNoQueue ? [NO_QUEUE_SENTINEL] : []), ...ids]);

  const handleToggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    emit(next, noQueueSelected);
  };

  return (
    <FilterPopover
      options={options}
      selectedIds={selectedIds}
      onToggleOption={handleToggle}
      onSelectAll={(ids) => emit(ids, noQueueSelected)}
      onClear={() => onChange([])}
      noneOption={
        showNoQueue
          ? {
              label: i18n.t("ticketsQueueSelect.noQueue"),
              selected: noQueueSelected,
              onToggle: () => emit(selectedIds, !noQueueSelected),
            }
          : undefined
      }
      placeholder={placeholder || i18n.t("tickets.filters.queues")}
      title={i18n.t("tickets.filters.queuesPopoverTitle")}
      searchPlaceholder={i18n.t("tickets.filters.queuesSearchPlaceholder")}
      emptyLabel={i18n.t("tickets.filters.noQueuesFound")}
      singleSelectedLabel={i18n.t("tickets.filters.singleSelected")}
      multipleSelectedLabel={i18n.t("tickets.filters.multipleSelected")}
      showingAllLabel={i18n.t("tickets.filters.noQueueSelected")}
      selectAllLabel={i18n.t("tickets.filters.selectAll")}
      clearLabel={i18n.t("tickets.filters.clear")}
      fullWidth={fullWidth}
      disabled={disabled}
      className={className}
      placement={placement}
    />
  );
}
